/**
 * Media prefetch coordinator.
 *
 * Module-level singleton that primes the browser's HTTP cache with HLS
 * manifests, posters, and first-segment bytes so that opening a lesson feels
 * instant. Three callers funnel into one queue:
 *
 *   1. useCoursePrefetch        — fires on course/lesson route mount, primes the
 *                                  full current course (tier 1 for every lesson).
 *   2. useNextLessonPrefetch    — fires on lesson route mount, primes tier 2
 *                                  (first segment) for the next 1–2 lessons.
 *   3. useInProgressCoursesPrefetch — lowest priority. For other in-progress
 *                                  courses the user has, primes the course
 *                                  thumbnail + lesson posters already in the
 *                                  enrollment payload.
 *
 * Invariants
 *   - Single download per URL per session (Set-backed dedup).
 *   - Concurrency capped at 3 parallel fetches.
 *   - Total bytes per session capped at 30 MB.
 *   - Skip everything on slow connection / Save-Data.
 *   - Aborting a scope cancels in-flight + drops pending entries for that scope.
 *   - All fetches use `cache: 'force-cache'` semantics via the browser HTTP
 *     cache. R2's existing Cache-Control headers govern actual eviction.
 *
 * Priority numbers (lower = runs first):
 *   1 — current course tier 1            (manifest + variant playlist + poster)
 *   2 — next lesson tier 2                (first 480p segment)
 *   3 — current course remaining tier 2   (after next-lesson boost)
 *   5 — other in-progress courses (thumbnails + lesson posters)
 */

import { withCacheBust } from '@/lib/feature-flags';

const MAX_PARALLEL_FETCHES = 3;
const SESSION_BUDGET_BYTES = 30 * 1024 * 1024; // 30 MB

/**
 * A separate byte allowance for one kind of prefetch (Speedies get their own 10 MB, F-129), so it
 * never eats into the lessons' 30 MB. Callers that pass none share the session budget, as before.
 */
export interface PrefetchBudget {
  key: string;
  limitBytes: number;
}

interface QueueEntry {
  url: string;
  priority: number;
  estimateBytes: number;
  signal: AbortSignal;
  budgetKey: string | null;
}

class PrefetchCoordinator {
  /** URLs that are in-flight OR completed this session — prevents duplicate fetches. */
  private readonly seen = new Set<string>();
  private readonly queue: QueueEntry[] = [];
  private inFlight = 0;
  private totalBytes = 0;
  /** Bytes per separate budget (see PrefetchBudget). */
  private readonly budgetBytes = new Map<string, number>();
  private kicker: ReturnType<typeof queueMicrotask> | null = null;

  /**
   * Enqueue a URL for prefetch at the given priority. No-op if:
   *   - URL is already seen (dedup)
   *   - Signal already aborted
   *   - Connection is slow / Save-Data is on
   *   - Adding `estimateBytes` would blow the session budget
   */
  enqueue(url: string, priority: number, estimateBytes: number, signal: AbortSignal, budget?: PrefetchBudget): void {
    if (signal.aborted) return;
    if (this.seen.has(url)) return;
    if (!isConnectionAllowed()) return;
    if (budget) {
      if ((this.budgetBytes.get(budget.key) ?? 0) + estimateBytes > budget.limitBytes) return;
    } else if (this.totalBytes + estimateBytes > SESSION_BUDGET_BYTES) {
      return;
    }

    this.seen.add(url);
    this.queue.push({ url, priority, estimateBytes, signal, budgetKey: budget?.key ?? null });
    this.scheduleKick();
  }

  /**
   * Bulk-enqueue. Same gating as `enqueue` applied per URL.
   */
  enqueueAll(urls: string[], priority: number, estimateBytes: number, signal: AbortSignal, budget?: PrefetchBudget): void {
    for (const url of urls) this.enqueue(url, priority, estimateBytes, signal, budget);
  }

  /** Read-only stats — exposed for debugging. */
  stats(): { seen: number; pending: number; inFlight: number; totalBytes: number; budgets: Record<string, number> } {
    return {
      seen: this.seen.size,
      pending: this.queue.length,
      inFlight: this.inFlight,
      totalBytes: this.totalBytes,
      budgets: Object.fromEntries(this.budgetBytes),
    };
  }

  private count(entry: QueueEntry, bytes: number): void {
    if (entry.budgetKey) this.budgetBytes.set(entry.budgetKey, (this.budgetBytes.get(entry.budgetKey) ?? 0) + bytes);
    else this.totalBytes += bytes;
  }

  private scheduleKick(): void {
    if (this.kicker !== null) return;
    this.kicker = queueMicrotask(() => {
      this.kicker = null;
      this.kick();
    }) as unknown as ReturnType<typeof queueMicrotask>;
  }

  private kick(): void {
    // Drop aborted entries before each dispatch round.
    while (this.queue.length > 0 && this.queue[0].signal.aborted) {
      this.queue.shift();
    }
    while (this.inFlight < MAX_PARALLEL_FETCHES && this.queue.length > 0) {
      // Pick the lowest-priority-number entry. O(n) is fine — queue stays small.
      let bestIdx = 0;
      for (let i = 1; i < this.queue.length; i++) {
        if (this.queue[i].priority < this.queue[bestIdx].priority) bestIdx = i;
      }
      const entry = this.queue.splice(bestIdx, 1)[0];
      if (entry.signal.aborted) continue;
      this.inFlight++;
      this.fetchOne(entry).finally(() => {
        this.inFlight--;
        // Re-kick — more queue or more capacity.
        this.scheduleKick();
      });
    }
  }

  private async fetchOne(entry: QueueEntry): Promise<void> {
    try {
      const response = await fetch(entry.url, {
        method: 'GET',
        cache: 'force-cache',
        signal: entry.signal,
        // Hint to the browser this is a low-priority prefetch — Chrome honours
        // this for HTTP/2 prioritisation; harmless elsewhere.
        priority: 'low' as RequestPriority,
      });
      // Drain to populate the HTTP cache.
      if (response.ok) {
        const contentLength = Number(response.headers.get('Content-Length') ?? 0);
        if (contentLength > 0) {
          this.count(entry, contentLength);
        } else {
          // Unknown length — drain body, count bytes from blob size.
          const blob = await response.blob();
          this.count(entry, blob.size);
        }
      }
    } catch (err) {
      // AbortError is expected on route unmount. Anything else is logged once
      // and swallowed — prefetch must never break the foreground experience.
      if ((err as DOMException)?.name !== 'AbortError') {
        // eslint-disable-next-line no-console
        console.debug('[media-prefetch] fetch failed', entry.url, err);
      }
    }
  }
}

/** Module singleton — survives Hot Module Replacement boundaries via window cache. */
function getCoordinator(): PrefetchCoordinator {
  const key = '__mediaPrefetchCoordinator__';
  const w = window as unknown as Record<string, PrefetchCoordinator>;
  if (!w[key]) w[key] = new PrefetchCoordinator();
  return w[key];
}

export const mediaPrefetchCoordinator = typeof window !== 'undefined'
  ? getCoordinator()
  : null;

/**
 * Connection gating. Returns false when:
 *   - Save-Data is on
 *   - Effective connection type is 2g / slow-2g / 3g
 * Returns true on fast networks and when navigator.connection is undefined
 * (e.g. Safari, Firefox — which lie or omit the API).
 */
export function isConnectionAllowed(): boolean {
  if (typeof navigator === 'undefined') return false;
  const conn = (navigator as unknown as {
    connection?: { effectiveType?: string; saveData?: boolean };
  }).connection;
  if (!conn) return true; // No info → don't penalise.
  if (conn.saveData === true) return false;
  if (conn.effectiveType && conn.effectiveType !== '4g') return false;
  return true;
}

// ────────────────────────────────────────────────────────────────────────────
// URL builders. Centralised so the prefetch hooks and the player resolve URLs
// from the same source of truth (resolveMediaUrl → VITE_MEDIA_HOST + bare R2
// path). Paths in the DB are bare (no host) — resolveMediaUrl prepends the
// configured host. Full URLs (legacy rows) pass through unchanged.
// ────────────────────────────────────────────────────────────────────────────

interface MediaAssetForPrefetch {
  playback_url: string | null;
  poster_url?: string | null;
  small_poster_url?: string | null;
  /** Ticket 86d40p593. 'ready' means the video is served by Cloudflare Stream. */
  stream_status?: string | null;
}

/**
 * Both tiers derive their URLs by treating `playback_url` as an HLS master
 * playlist and appending `/480p/...` to its directory. Two states break that,
 * and both are about to become common:
 *
 *   1. A row served by Cloudflare Stream. Its address is a signed, short-lived
 *      pass that `useStreamPass` renews; prefetching it would warm a URL the
 *      player will never request again, and `withCacheBust` would corrupt the
 *      token in the path. Nothing to prefetch — Cloudflare does the warming.
 *
 *   2. A `playback_url` that is not a ladder at all. Event recordings carry the
 *      bare MP4 (`/drive-ingest/<id>/video.mp4`, written by finalize-recording),
 *      and the `/master\.m3u8$/` replace below is a no-op on it — so `dir`
 *      becomes the MP4 itself, tier 1 emits the WHOLE FILE as its first URL, and
 *      the two derived playlist/segment URLs 404. That is hundreds of megabytes
 *      warmed for nothing.
 *
 * So both tiers refuse anything that is not a master playlist on a row that is
 * not on Stream. Returning [] is the correct no-op — every caller already
 * handles an empty list.
 */
function isPrefetchable(
  asset: MediaAssetForPrefetch | null | undefined,
): asset is MediaAssetForPrefetch & { playback_url: string } {
  if (!asset?.playback_url) return false;
  if (asset.stream_status === 'ready') return false;
  return /\/master\.m3u8(\?|$)/.test(asset.playback_url);
}

/** Tier 1: manifest + 480p variant playlist + poster. ~50–100 KB total. */
export function tier1UrlsForAsset(asset: MediaAssetForPrefetch | null | undefined): string[] {
  if (!isPrefetchable(asset)) return [];
  const masterUrl = resolveMediaUrlInternal(asset.playback_url);
  if (!masterUrl) return [];
  // Variant playlist sits next to master.m3u8 — replace the trailing filename.
  // NB: derive `dir` from the RAW master (before any cache-buster), then emit the
  // busted master so the player and prefetcher request the identical URL.
  const dir = masterUrl.replace(/\/master\.m3u8$/, '');
  // Bust master + variant playlist (not the poster) so the warmed URLs match
  // exactly what the hls.js loader requests when the flag is on.
  const out = [withCacheBust(masterUrl), withCacheBust(`${dir}/480p/playlist.m3u8`)];
  const posterUrl = resolveMediaUrlInternal(asset.poster_url);
  if (posterUrl) out.push(posterUrl);
  return out;
}

/** Tier 2: first 480p segment. ~1 MB. */
export function tier2UrlsForAsset(asset: MediaAssetForPrefetch | null | undefined): string[] {
  if (!isPrefetchable(asset)) return [];
  const masterUrl = resolveMediaUrlInternal(asset.playback_url);
  if (!masterUrl) return [];
  const dir = masterUrl.replace(/\/master\.m3u8$/, '');
  return [withCacheBust(`${dir}/480p/segment_000.ts`)];
}

/**
 * Resolve a bare media path to a full URL via VITE_MEDIA_HOST. Mirrors
 * `resolveMediaUrl` in `lib/media.ts` — duplicated here to keep
 * `media-prefetch.ts` free of cross-module dependencies that would make
 * tree-shaking awkward.
 */
function resolveMediaUrlInternal(path: string | null | undefined): string | null {
  if (!path) return null;
  if (path.startsWith('http://') || path.startsWith('https://')) return path;
  const host = (import.meta as unknown as { env?: { VITE_MEDIA_HOST?: string } }).env
    ?.VITE_MEDIA_HOST ?? '';
  return `${host}${path}`;
}

/** Resolve a poster/thumbnail bare path → full URL. Returns null on empty input. */
export function resolvePrefetchUrl(path: string | null | undefined): string | null {
  return resolveMediaUrlInternal(path);
}

/** Estimate per-URL byte size — fed into the budget check before fetching. */
export const ESTIMATE_BYTES = {
  manifest: 2 * 1024, // ~2 KB
  variantPlaylist: 4 * 1024, // ~4 KB
  poster: 50 * 1024, // ~50 KB (estimated, real R2 reads will be ~100 KB)
  firstSegment: 1 * 1024 * 1024, // ~1 MB
} as const;

/**
 * Priority constants — kept here so callers don't sprinkle magic numbers.
 */
export const PREFETCH_PRIORITY = {
  currentCourseTier1: 1,
  nextLessonTier2: 2,
  currentCourseTier2: 3,
  otherInProgressCourse: 5,
} as const;
